/*
 * メッセージボックスを表示するためのUtilクラス
 * 
 * [使い方]
 * ★通常メッセージ
 * import { MessageUtil } from '@/models/Message/MessageUtil';
 * 
 * private onOK(): void {
 *      // 任意のコールバック関数を定義
 *      alert("OK押下");
 * }
 * 
 * private hogehoge(): void {
 *      // 呼び出し
 *      MessageUtil.MessageBox("test", this.onOK, null, MessageUtil.MB_ICON_INFO);
 * }
 * 
 * 
 * 
 * ★確認メッセージ
 * import { MessageUtil } from '@/models/Message/MessageUtil';
 *
 * private onOK(): void {
 *      // 任意のコールバック関数を定義
 *      alert("OK押下");
 * }
 * 
 * private onCancel(): void {
 *      // 任意のコールバック関数を定義
 *      alert("キャンセル押下");
 * }
 *
 * private hogehoge(): void {
 *      // 呼び出し
 *      MessageUtil.MessageBox("test", this.onOK, this.onCancel, MessageUtil.MB_YESNO);
 * }
 */
import { MessageType } from '@/models/Message/MessageType';


export class MessageUtil {

    // メッセージの種類
    public static MB_YESNO = 0x00000001;    // 確認メッセージ

    // アイコン
    public static MB_ICON_INFO = 0x00000002;    // 情報アイコン
    public static MB_ICON_WARNING = 0x00000004; // 警告アイコン
    public static MB_ICON_ERROR = 0x00000008;   // エラーアイコン
    public static MB_ICON_NONE = 0x00000020;   // アイコンなし

    // フォーカス
    public static MB_BTN_FOCUS_CANCEL = 0x00000010; // キャンセル

    /*
     * メッセージの表示
     * @param[in] strHTML : メッセージ内容(HTML)
     * 
     * @param[in] callbackOK : OK押下後のコールバック関数
     *              ★Functionクラスで指定すること。コールバック不要ならnull
     *              
     * @param[in] callbackCancel : キャンセル押下後のコールバック関数
     *              ★MB_YESNOが立っているときのみ有効
     *              ★Functionクラスで指定すること。コールバック不要ならnull
     *              
     * @param[in] bit : オプションビット
     * 
     * @param[in] caption : タイトル(undefined: デフォルト ※アイコンに合わせたタイトル)
     * @param[in] okText : OK(確認メッセージでは"はい")ボタンの文言(undefined: デフォルト)
     * @param[in] cancelText :  確認メッセージの"いいえ"ボタンの文言(undefined: デフォルト)
     *                          ★MB_YESNOが立っているときのみ有効
     * @param[in] isTopMost : ローディングよりも上に出したい場合に使用。（基本使用禁止。セッションタイムアウトによるログアウトメッセージ等、強制的に表示したいときのみ使用）
     */
    public static MessageBox(strHTML: string, callbackOK: any, callbackCancel: any, bit: number, caption: string | undefined = undefined, okText: string | undefined = undefined, cancelText: string | undefined = undefined, isTopMost:boolean = false): void {

        if (0 != (bit & MessageUtil.MB_YESNO)) {
            // 確認メッセージ
            if (undefined == caption) {
                caption = "確認";
            }
            MessageUtil.ConfirmBox(caption, strHTML, callbackOK, callbackCancel, bit, okText, cancelText, isTopMost);
            return;
        }

        // 通常メッセージ
        if (undefined == MessageUtil.messageRef) {
            return;
        }

        // アイコン指定
        let type = MessageType.Information;
        if (0 != (bit & MessageUtil.MB_ICON_WARNING)) {
            type = MessageType.Warning;
        }
        if (0 != (bit & MessageUtil.MB_ICON_ERROR)) {
            type = MessageType.Error;
        }

        // アイコン非表示
        if (0 != (bit & MessageUtil.MB_ICON_NONE)) {
            MessageUtil.messageRef.HideIcon();
        }

        // 画面表示
        MessageUtil.messageRef.Show(strHTML, type, callbackOK, caption, okText, isTopMost);
    }

    /*
     * 確認メッセージの表示
     * @param[in] strTitle : タイトル
     * @param[in] strText : メッセージ内容(HTML)
     * @param[in] callbackOK : OK押下後のコールバック関数
     * @param[in] callbackCancel : キャンセル押下後のコールバック関数
     * @param[in] bit : オプションビット
     * @param[in] okText : OKボタンの文言
     * @param[in] cancelText : キャンセルボタンの文言
     * @param[in] isTopMost : ローディングよりも上に出したい場合に使用。（基本使用禁止。セッションタイムアウトによるログアウトメッセージ等、強制的に表示したいときのみ使用）
     */
    private static ConfirmBox(strTitle: string, strText: string, callbackOK: any, callbackCancel: any, bit: number, okText: string | undefined = undefined, cancelText: string | undefined = undefined, isTopMost: boolean = false): void {
        if (undefined == MessageUtil.confirmRef) {
            return;
        }

        // フォーカス指定
        MessageUtil.confirmRef.SetCancelFocus(0 != (bit & MessageUtil.MB_BTN_FOCUS_CANCEL));

        // アイコン非表示
        if (0 != (bit & MessageUtil.MB_ICON_NONE)) {
            MessageUtil.confirmRef.HideIcon();
        }
        if (0 != (bit & MessageUtil.MB_ICON_WARNING)) {
            MessageUtil.confirmRef.WarningIcon();
        }

        // 画面表示
        MessageUtil.confirmRef.Show(strTitle, strText, callbackOK, callbackCancel, okText, cancelText, isTopMost);
    }

    /*
     * メッセージを隠匿する
     */
    public static HideMessage() : void {
        if (undefined != MessageUtil.messageRef) {
            MessageUtil.messageRef.Hide();
        }
        if (undefined != MessageUtil.confirmRef) {
            MessageUtil.confirmRef.Hide();
        }
    }

    /*
     * メッセージを隠匿する
     * メッセージ終了時のコールバック処理が完了するまで待機する場合に使用
     */
    public static async HideMessageAsync(): Promise<void> {
        if (undefined != MessageUtil.messageRef) {
            await MessageUtil.messageRef.HideAsync();
        }
        if (undefined != MessageUtil.confirmRef) {
            await MessageUtil.confirmRef.HideAsync();
        }
    }

    /*
     * メッセージの表示
     * @param[in] strHTML : メッセージ内容(HTML)
     * 
     * @param[in] callbackOK : OK押下後のコールバック関数
     *              ★Functionクラスで指定すること。コールバック不要ならnull
     *              
     * @param[in] callbackCancel : キャンセル押下後のコールバック関数
     *              ★MB_YESNOが立っているときのみ有効
     *              ★Functionクラスで指定すること。コールバック不要ならnull
     *              
     * @param[in] bit : オプションビット
     * 
     * @param[in] caption : タイトル(undefined: デフォルト ※アイコンに合わせたタイトル)
     * @param[in] okText : OK(確認メッセージでは"はい")ボタンの文言(undefined: デフォルト)
     * @param[in] cancelText :  確認メッセージの"いいえ"ボタンの文言(undefined: デフォルト)
     *                          ★MB_YESNOが立っているときのみ有効
     * @param[in] isTopMost : ローディングよりも上に出したい場合に使用。（基本使用禁止。セッションタイムアウトによるログアウトメッセージ等、強制的に表示したいときのみ使用）
     * @retuens   OKならtrue、それ以外ならfalse
     */
    public static async MessageBoxAsync(strHTML: string, callbackOK: any, callbackCancel: any, bit: number, caption: string | undefined = undefined, okText: string | undefined = undefined, cancelText: string | undefined = undefined, isTopMost: boolean = false): Promise<boolean> {
        return new Promise((resolve) => {
            const wrapCallbackOK = function () {
                if (callbackOK) {
                    callbackOK();
                }
                resolve(true);
            };

            const wrapCallbackCancel = function () {
                if (callbackCancel) {
                    callbackCancel();
                }
                resolve(false);
            }

            this.MessageBox(strHTML, wrapCallbackOK, wrapCallbackCancel, bit, caption, okText, cancelText, isTopMost);
        });

    }

    /*
     * 表示するメッセージのrefs
     */
    private static messageRef: any | undefined = undefined;
    /*
     * 表示する確認メッセージのrefs
     */
    private static confirmRef: any | undefined = undefined;

    /* 
     * メッセージの参照を登録
     * ★App.vue意外から呼び出さないこと★
     */
    public static SetMessageBox(ref: any): void {
        MessageUtil.messageRef = ref;
    }

    /* 
     * 確認メッセージの参照を登録
     * ★App.vue意外から呼び出さないこと★
     */
    public static SetConfirmBox(ref: any): void {
        MessageUtil.confirmRef = ref;
    }
}